/**
 * South African bank account format validation (wallet UI).
 * Mirrors services/disbursement/saBankAccountValidation.js
 */

export const BANK_ACCOUNT_RULES: Record<string, { min: number; max: number; label: string }> = {
  ABSA: { min: 9, max: 10, label: '9 or 10' },
  'ABSA Bank': { min: 9, max: 10, label: '9 or 10' },
  FNB: { min: 11, max: 11, label: '11' },
  'First National Bank (FNB)': { min: 11, max: 11, label: '11' },
  'First National Bank': { min: 11, max: 11, label: '11' },
  Nedbank: { min: 10, max: 10, label: '10' },
  'Standard Bank': { min: 9, max: 11, label: '9 or 11' },
  Capitec: { min: 10, max: 10, label: '10' },
  'Capitec Bank': { min: 10, max: 10, label: '10' },
  'African Bank': { min: 10, max: 11, label: '10 or 11' },
  'Discovery Bank': { min: 10, max: 11, label: '10 or 11' },
  Investec: { min: 11, max: 11, label: '11' },
  'Investec Bank': { min: 11, max: 11, label: '11' },
  TymeBank: { min: 10, max: 11, label: '10 or 11' },
  'Bidvest Bank': { min: 10, max: 11, label: '10 or 11' },
};

export function normalizeBankAccountNumber(accountNumber: string, bankName?: string): string {
  const digits = String(accountNumber || '').replace(/\D/g, '');
  if (!digits) return '';
  const rules = bankName ? BANK_ACCOUNT_RULES[bankName] : null;
  const maxLen = rules?.max ?? 11;
  if (digits.length > maxLen && digits.startsWith('0')) {
    const stripped = digits.replace(/^0+/, '');
    return stripped.length < maxLen ? digits.slice(digits.length - maxLen) : stripped;
  }
  return digits;
}

export const INSTANT_PAYMENT_CARD_PAN_ERROR =
  'This looks like a card number, not a bank account number. Instant Payment needs the account number from your bank app (not the number on the plastic). The payment was not sent.';

export const BANK_ACCOUNT_IS_MOBILE_ERROR =
  'This looks like a cellphone number, not a bank account number. Enter the account number from your bank app (not your mobile number). The payment was not sent.';

export const MOBILE_IS_BANK_ACCOUNT_ERROR =
  "This looks like a bank account number, not a cellphone. To send to a MyMoolah wallet, use the recipient's mobile number.";

export const BANK_ACCOUNT_IS_MOBILE = 'BANK_ACCOUNT_IS_MOBILE';
export const MOBILE_IS_BANK_ACCOUNT = 'MOBILE_IS_BANK_ACCOUNT';

export function isLikelySaMobileMsisdn(value?: string | null): boolean {
  const digits = String(value || '').replace(/\D/g, '');
  if (/^0[6-8]\d{8}$/.test(digits)) return true;
  if (/^27[6-8]\d{8}$/.test(digits)) return true;
  return false;
}

export function isLikelyBankAccountAsMobile(value?: string | null): boolean {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return false;
  if (isLikelySaMobileMsisdn(digits)) return false;
  if (isLikelyCardPan(digits)) return true;
  return digits.length >= 9 && digits.length <= 12;
}

export function validateSaBankAccountNumber(
  accountNumber: string,
  opts: { bankName?: string } = {}
): { valid: boolean; error: string | null; normalized: string; code?: string | null } {
  const { bankName } = opts;
  const compact = String(accountNumber || '').replace(/\s/g, '');

  if (!compact) {
    return { valid: false, error: 'Account number is required', normalized: '', code: null };
  }

  if (isLikelySaMobileMsisdn(accountNumber) || isLikelySaMobileMsisdn(compact)) {
    return {
      valid: false,
      error: BANK_ACCOUNT_IS_MOBILE_ERROR,
      normalized: String(accountNumber || '').replace(/\D/g, ''),
      code: BANK_ACCOUNT_IS_MOBILE,
    };
  }

  const normalized = normalizeBankAccountNumber(accountNumber, bankName);

  if (!normalized) {
    return { valid: false, error: 'Account number is required', normalized: '', code: null };
  }
  if (!/^\d+$/.test(normalized)) {
    return { valid: false, error: 'Account number must contain digits only', normalized, code: null };
  }
  if (/^(\d)\1+$/.test(normalized)) {
    return { valid: false, error: 'Account number does not look valid', normalized, code: null };
  }

  if (isLikelyCardPan(normalized) || isLikelyCardPan(compact)) {
    return {
      valid: false,
      error: INSTANT_PAYMENT_CARD_PAN_ERROR,
      normalized,
      code: 'INSTANT_PAYMENT_CARD_PAN',
    };
  }

  const rules = bankName ? BANK_ACCOUNT_RULES[bankName] : null;
  if (rules) {
    if (normalized.length < rules.min || normalized.length > rules.max) {
      return {
        valid: false,
        error: `${bankName} accounts use ${rules.label} digits (you entered ${normalized.length}). Use your bank account number, not a card number.`,
        normalized,
        code: null,
      };
    }
  } else if (normalized.length < 6 || normalized.length > 20) {
    return { valid: false, error: 'Account number must be 6–20 digits', normalized, code: null };
  }

  return { valid: true, error: null, normalized, code: null };
}

export function bankFieldModalCopy(result: {
  valid: boolean;
  error: string | null;
  code?: string | null;
}): { title: string; message: string } | null {
  if (result.valid || !result.error) return null;
  if (result.code === BANK_ACCOUNT_IS_MOBILE) {
    return { title: 'This looks like a cellphone number', message: result.error };
  }
  if (result.code === 'INSTANT_PAYMENT_CARD_PAN') {
    return { title: 'This looks like a card number', message: result.error };
  }
  return { title: 'Invalid account number', message: result.error };
}

export function walletDestinationModalCopy(value?: string | null): { title: string; message: string } | null {
  if (!isLikelyBankAccountAsMobile(value)) return null;
  return { title: 'This looks like a bank account number', message: MOBILE_IS_BANK_ACCOUNT_ERROR };
}

export const INSTANT_PAYMENT_IRREVERSIBLE_NOTICE = {
  type: 'warning' as const,
  title: 'Successful Instant Payments cannot be reversed',
  description:
    'If this payment succeeds, money sent to the wrong account cannot be recalled. Check the account number before you proceed.',
};

export const INSTANT_PAYMENT_RAIL_HINT =
  'Successful Instant Payments cannot be reversed. Use the account number from your bank app, not a card number.';

function luhnValid(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits[i]);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/** 13–19 digit Luhn-valid numbers are card PANs (and 13-digit SA IDs), not Instant Payment accounts. */
export function isLikelyCardPan(accountNumber: string): boolean {
  const digits = String(accountNumber || '').replace(/\D/g, '');
  if (digits.length < 13 || digits.length > 19) return false;
  return luhnValid(digits);
}

export function assertInstantPaymentAccountNotCardPan(accountNumber: string): void {
  if (isLikelyCardPan(accountNumber)) {
    throw new Error(INSTANT_PAYMENT_CARD_PAN_ERROR);
  }
}

export function buildInstantPaymentNotices(
  accountResolution?: { resolved?: boolean } | null,
): Array<{ type: 'warning'; title: string; description: string }> {
  const notices = [INSTANT_PAYMENT_IRREVERSIBLE_NOTICE];
  if (accountResolution && !accountResolution.resolved) {
    notices.push({
      type: 'warning',
      title: 'Bank could not verify the account name',
      description: 'Please confirm the account number is correct before proceeding.',
    });
  }
  return notices;
}
